import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

/**
 * RenameSpeakerModal Component
 * 
 * Modal for renaming a speaker across all transcript blocks
 */
const RenameSpeakerModal = ({ isOpen, onClose, onRename, currentName }) => {
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && currentName) {
      setNewName(currentName);
    }
  }, [isOpen, currentName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const trimmedName = newName.trim();
    
    if (!trimmedName) {
      setError('Speaker name cannot be empty');
      return;
    }
    
    if (trimmedName === currentName) {
      setError('New name must be different from current name');
      return;
    }
    
    if (trimmedName.length < 2) {
      setError('Speaker name must be at least 2 characters');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await onRename(currentName, trimmedName);
      setNewName('');
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to rename speaker');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewName('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
      style={{ zIndex: 100000 }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Rename Speaker</h3>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label htmlFor="current-name" className="block text-sm font-medium text-gray-700 mb-1">
              Current Name
            </label>
            <input
              id="current-name"
              type="text"
              value={currentName}
              disabled
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-600"
            />
          </div>

          <div>
            <label htmlFor="new-name" className="block text-sm font-medium text-gray-700 mb-1">
              New Name
            </label>
            <input
              id="new-name"
              type="text"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                setError('');
              }}
              placeholder="Enter new speaker name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
              autoFocus
            />
            {error && (
              <p className="mt-1 text-sm text-red-600">{error}</p>
            )}
          </div>

          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              ⚠️ This will rename the speaker across all transcript blocks.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Renaming...' : 'Rename Speaker'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RenameSpeakerModal;

